import React, { useEffect, useState, useCallback, useRef } from "react";
import {
    CardContent,
    CardHeader,
    Grid,
    TextField,Stack,
    Button, TableContainer, TableHead, TableRow, TableCell, Table, TableBody, Avatar,
    Box, Typography, Chip, InputAdornment, IconButton, Tooltip
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { styled } from '@mui/material/styles';
import {Loader, FormCard} from "../../../../../Components";
import { fetchUsers, } from "../../../../Core/Users";
import useDeepEqualSelector from "../../../../../Hooks/useDeepEqualSelector";
import {usePermissions} from "../../../../../Hooks/usePermissions";
import { setModal } from "../../../../Common/App";
import { AssignUserToDepartment } from "../../../../Core/Users/Components/Forms/AssignUserToDepartment";
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';    
import { UserEventModal } from "../../../../Core/Users";
import { ConfirmUserRemovalModal } from "../Modals/ConfirmUserRemovalModal";


const StyledTable = styled(Table)(({ theme }) => ({
    '& thead th': {
        color: theme.text.dark,
        fontWeight: 600,
        fontSize: '0.8rem',
        letterSpacing: 0.2,
    },
    '& tbody tr': {
        transition: 'background-color 0.2s ease',
    },
    '& tbody tr:hover': {
        backgroundColor: theme.palette.action.hover,
    },
    '& td, & th': {
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    }
}));

export const UsersTab = ({isLoading, setIsLoading}) => {

    const dispatch = useDispatch();
    const {hasPermission, hasAnyPermission } = usePermissions();
    const item = useSelector((store) => store.departments.selected);
    const currentUser = useSelector((store) => store.auth.user);
    const items = useSelector((store) => store.users.list);
    const [, setErrors] = useState({});
    const [, setMessage] = useState("");
    const [, setStatus] = useState("");
    const [, setSearch] = useState("");
    const [searchQuery, setSearchQuery] = useState(""); // Separate state for actual search
    const filters = useSelector((store) => store.users.config.filters);
    const pagination = useDeepEqualSelector((store) => store.users.config.pagination);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        await fetchUsers({dispatch, filters:{ ...filters, ...pagination, department_id: item.id, keyword: searchQuery, page:0, pageSize:100}}).then((res) => {
            setIsLoading(false);
            setErrors(res.errors)
            setMessage(res.message);
            setStatus(res.status);
        });
    }, [item.id, dispatch, searchQuery, filters, pagination, setIsLoading]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRemoveUser = async (user) => {
        dispatch(setModal({component: <ConfirmUserRemovalModal user={user} department_id={item.id} />, open: true}));
    };

    const searchTimeoutRef = useRef(null);

    const handleSearch = (value) => {
        setSearch(value); 
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        if (value.length >= 3 || value.length === 0) {
            searchTimeoutRef.current = setTimeout(() => {
                setSearchQuery(value);
            }, 500);
        }
    };

    const handleAddUser = () => {
        dispatch(setModal({component: <AssignUserToDepartment />, open: true}));
    }

    const handleEventChange = (user) => {
        dispatch(setModal({component: <UserEventModal user={user} />, open: true}));
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <FormCard>
                    <CardHeader
                        title="Users"
                        subheader="Department Subheader"
                        action={
                            <Stack direction="row" spacing={1}>
                                {hasPermission('roster.department.add-users') && (
                                    <Button variant="standard" color="primary" onClick={() => handleAddUser()}>Add User</Button>
                                )}
                                <TextField
                                    placeholder="Search users..."
                                    variant="outlined"
                                    size="small"
                                    onChange={(e) => handleSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">🔎</InputAdornment>
                                    }}
                                />
                            </Stack>
                        }
                        avatar={<Loader isLoading={isLoading} type={"linear"}/>}
                    />
                    <CardContent>
                        <TableContainer>
                            <StyledTable>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell align="center">Event</TableCell>
                                        {hasAnyPermission(['roster.department.remove-user','roster.department.remove-own-users']) && !item.is_parking && (
                                            <TableCell align="right">Action</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items && items.length > 0 ? (
                                        items.map((user) => (
                                            <TableRow key={user.id} hover>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <Avatar
                                                            src={user.avatar_url || user.avatar || '/images/user-icon-3.jpg'}
                                                            alt={`${user.first_name} ${user.last_name}`}
                                                            sx={{ width: 36, height: 36 }}
                                                        >
                                                            {(user.first_name?.[0] || '').toUpperCase()}
                                                        </Avatar>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                                                                <Typography variant="subtitle2" fontWeight={400}>
                                                                    {user.first_name} {user.last_name}
                                                                </Typography>
                                                                {user.id === item.manager_id && (
                                                                    <Chip 
                                                                        label="Manager" 
                                                                        color="primary" 
                                                                        size="small"
                                                                        variant="outlined"
                                                                    />
                                                                )}
                                                            </Box>
                                                            {user.employee_id && (
                                                                <Typography variant="caption" color="text.secondary">
                                                                    ID: {user.employee_id}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">{user.event || '-'}</TableCell>
                                                <TableCell align="right">
                                                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="Choose Event">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleEventChange(user)}
                                                            >
                                                                <EventIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {hasAnyPermission(['roster.department.remove-user','roster.department.remove-own-users']) && user.id !== item.manager_id && user.id !== currentUser?.id && !item.is_parking && (
                                                            <Tooltip title="Remove User">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleRemoveUser(user)}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No users found in this department.
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                    Use the "Add User" button to add users to this department.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </StyledTable>
                        </TableContainer>
                    </CardContent>
                </FormCard>
            </Grid>
        </Grid>
    );
};
